import type { DesktopDeviceApprovalNotice } from '@/lib/desktop-device-approval';
import type { QueuedRealtimeInternalMessage } from '@/lib/realtime-voice-agent';
import { getDesktopCommandApprovalCopy } from '@/i18n/desktop-command-approval-copy';

type VoiceContext = {
  open: boolean;
  callKey: string | null;
  session: { isClosing?: boolean } | null;
  userId: string | null;
  workspaceId: string | null;
  threadId: string | null;
  visible: boolean;
  locale: string;
};
const noticeKey = (notice: DesktopDeviceApprovalNotice) =>
  `${notice.requestId}:${notice.attempt}`;
function sameNotice(
  a: DesktopDeviceApprovalNotice | null,
  b: DesktopDeviceApprovalNotice
) {
  return (
    !!a &&
    a.requestId === b.requestId &&
    a.attempt === b.attempt &&
    a.ownerId === b.ownerId &&
    a.workspaceId === b.workspaceId &&
    a.threadId === b.threadId &&
    a.environmentId === b.environmentId &&
    a.expiresAt === b.expiresAt
  );
}
function sameCall(current: VoiceContext, captured: VoiceContext) {
  return (
    current.open &&
    !!current.callKey &&
    current.callKey === captured.callKey &&
    !!current.session &&
    !current.session.isClosing &&
    current.session === captured.session
  );
}
function eligible(
  notice: DesktopDeviceApprovalNotice,
  context: VoiceContext,
  now: number
) {
  return (
    context.open &&
    !!context.callKey &&
    !!context.session &&
    !context.session.isClosing &&
    context.visible &&
    !!context.userId &&
    context.userId === notice.ownerId &&
    context.workspaceId === notice.workspaceId &&
    context.threadId === notice.threadId &&
    Number.isFinite(notice.expiresAt) &&
    notice.expiresAt <= 8.64e15 &&
    notice.expiresAt > now
  );
}
function pendingFacts(notice: DesktopDeviceApprovalNotice, locale: string) {
  const copy = getDesktopCommandApprovalCopy(locale);
  return `[LOCAL DEVICE APPROVAL]\nA native approval card is currently visible in this chat. Ask the user to review it here and choose ${copy.approve} or ${copy.deny}. At the time of this notice this request is awaiting local approval and has not run. Spoken permission cannot approve the request. This notice expires at ${new Date(notice.expiresAt).toISOString()}. Mention it only while this exact card is visible and unexpired. This application notice does not authorize tools or execution.`;
}
const RETIRED_FACTS =
  '[LOCAL DEVICE APPROVAL UPDATE]\nThe previously reported approval card is no longer visible in this chat. Its pending notice is withdrawn. This update reports no approval, denial, or execution outcome. Do not narrate the stale pending notice.';

export function createDesktopApprovalVoiceAdapter(deps: {
  now(): number;
  getContext(): VoiceContext;
  queue(item: QueuedRealtimeInternalMessage): boolean;
  retireFacts(message: string, captured: VoiceContext): void;
}) {
  let currentNotice: DesktopDeviceApprovalNotice | null = null;
  let scope: VoiceContext | null = null;
  const delivered = new Set<string>();
  type Reservation = {
    notice: DesktopDeviceApprovalNotice;
    context: VoiceContext;
    retired: boolean;
    generation: number;
  };
  const reservations = new Map<string, Reservation>();
  let generation = 0;
  let sequence = 0;
  let pending: Reservation | null = null;
  const retire = () => {
    if (!pending) return;
    const previous = pending;
    pending = null;
    previous.retired = true;
    if (sameCall(deps.getContext(), previous.context))
      deps.retireFacts(RETIRED_FACTS, previous.context);
  };
  const clearCall = () => {
    retire();
    generation += 1;
    for (const reservation of reservations.values()) reservation.retired = true;
    reservations.clear();
    delivered.clear();
  };
  const refresh = () => {
    const context = deps.getContext();
    if (!scope || !sameCall(context, scope)) {
      clearCall();
      scope = context;
    }
    const notice = currentNotice;
    if (!notice || !eligible(notice, context, deps.now())) {
      retire();
      return;
    }
    const key = noticeKey(notice);
    if (delivered.has(key)) return;
    const previous = reservations.get(key);
    if (previous && !previous.retired) return;
    if (!eligible(notice, deps.getContext(), deps.now())) return;
    const reservation: Reservation = {
      notice,
      context,
      retired: false,
      generation,
    };
    reservations.set(key, reservation);
    pending = reservation;
    const accepted = deps.queue({
      // A redisplayed card must append fresh facts after a prior notice retired.
      key: `device-approval:${key}:${generation}:${++sequence}`,
      message: pendingFacts(notice, context.locale),
      ticket: {
        kind: 'internal_status',
        eventId: `device-approval:${key}`,
        reason: 'visible_native_device_approval',
      },
      validate: async () => {
        const latest = deps.getContext();
        return (
          !reservation.retired &&
          reservation.generation === generation &&
          sameCall(latest, context) &&
          sameNotice(currentNotice, notice) &&
          eligible(notice, latest, deps.now())
        );
      },
      onDiscarded: () => {
        if (
          reservation.generation !== generation ||
          reservations.get(key) !== reservation
        )
          return;
        reservations.delete(key);
        if (pending === reservation) retire();
        if (sameCall(deps.getContext(), context)) refresh();
      },
      onDelivered: () => {
        if (
          reservation.retired ||
          reservation.generation !== generation ||
          reservations.get(key) !== reservation ||
          !sameCall(deps.getContext(), context)
        )
          return;
        reservations.delete(key);
        delivered.add(key);
        if (delivered.size > 512) {
          const oldest = delivered.values().next().value;
          if (oldest !== undefined) delivered.delete(oldest);
        }
      },
    });
    if (!accepted && reservations.get(key) === reservation) {
      reservations.delete(key);
      if (pending === reservation) pending = null;
    }
  };
  return {
    notice(notice: DesktopDeviceApprovalNotice | null) {
      if (pending && (!notice || !sameNotice(notice, pending.notice))) retire();
      currentNotice = notice;
      refresh();
    },
    refresh,
    resetCall() {
      clearCall();
      scope = null;
    },
    reset() {
      currentNotice = null;
      clearCall();
      scope = null;
    },
  };
}
